import React from "react";
import { Link } from "react-router-dom";
import useScrollReveal from "../hooks/useScrollReveal";
import Icon from "../components/Icon";

export default function IslamicStudiesPage() {
  useScrollReveal();
  return (
    <>
      <section className="relative overflow-hidden pt-16 pb-20 md:pt-20 md:pb-32 bg-surface-bright">
        <div className="absolute inset-0 geometric-pattern-emerald opacity-40"></div>
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-primary-fixed opacity-20 blur-3xl rounded-full"></div>
        <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-tertiary-fixed opacity-20 blur-3xl rounded-full"></div>
        <div className="relative page-x max-w-max-width mx-auto flex flex-col md:flex-row items-center gap-8 md:gap-16">
          <div className="flex-1 space-y-6">
            <div className="inline-flex items-center gap-2 px-4 py-1 rounded-full bg-primary-container text-on-primary-container font-label-md text-label-md">
              <Icon name="stars" className="material-symbols-outlined text-sm" />
              Comprehensive Deen
            </div>
            <h1 className="text-display-responsive text-primary leading-tight">
              Islamic Studies Program
            </h1>
            <p className="font-body-lg text-body-lg text-on-surface-variant max-w-xl">
              Learn the foundations of Fiqh, Seerah, Akhlaq, and Hadith. Our structured curriculum provides families and youth with a holistic, values-based spiritual education for daily life.
            </p>
            <div className="flex flex-col xs:flex-row flex-wrap gap-3 sm:gap-4 pt-4">
              <Link
                to="/contact"
                className="inline-block bg-primary text-on-primary px-6 sm:px-8 py-3 sm:py-4 rounded-full w-full sm:w-auto text-center font-headline-md text-headline-md shadow-lg shadow-primary/20 hover:scale-105 active:scale-95 transition-all"
              >
                Start Free Trial
              </Link>
            </div>
          </div>
          <div className="flex-1 relative">
            <div className="rounded-3xl overflow-hidden shadow-2xl border-4 md:border-8 border-white">
              <img
                alt="Students learning Islamic values"
                className="w-full h-auto object-cover"
                src="https://lh3.googleusercontent.com/aida-public/AB6AXuDst3RHgpZmXnklUGvavZ2R2dPZGqy37UOV7CJ57PQCsAv_5xBgBWi4p-19I0HTZHM2y50-p8DcMGNjs7qaM5gqH5y27nWBzL2-3MTtMXHiFfdAr76M70stjhSZIM6WRUkZwyqNlewqHVfvNqEHigMFta5EO9cZC25rj1IV6xltiLLnps--1yksg2ZEXXWvnhzF4Qo-nzL60YVnRSWIgHHemQJNOS2ft9L-mxOfKqSIM6yn3_TwTrh2tRtFlrt_pHz-yEResRZt2W59"
              />
            </div>
            <div className="relative md:absolute md:-bottom-8 md:-left-8 mt-6 md:mt-0 bg-white p-4 sm:p-6 rounded-2xl shadow-xl border border-outline-variant flex items-center gap-4 max-w-[calc(100vw-2rem)] md:max-w-none">
              <div className="bg-tertiary-container p-3 rounded-full">
                <Icon
                  name="mosque"
                  className="material-symbols-outlined text-on-tertiary-container"
                  filled
                />
              </div>
              <div>
                <p className="font-label-md text-label-md text-on-surface-variant">
                  Holistic Focus
                </p>
                <p className="font-headline-md text-headline-md text-primary">
                  Character &amp; Knowledge
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section-y bg-white">
        <div className="page-x max-w-max-width mx-auto">
          <div className="text-center mb-10 md:mb-16 space-y-4">
            <h2 className="text-headline-responsive text-primary">
              Expected Outcomes
            </h2>
            <p className="font-body-md text-body-md text-on-surface-variant max-w-2xl mx-auto">
              Our comprehensive course content balances essential knowledge with character refinement.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-gutter">
            <div className="group card-pad rounded-2xl border border-outline-variant hover:border-primary hover:shadow-xl transition-all duration-300 scroll-reveal">
              <div className="w-16 h-16 bg-surface-container rounded-full flex items-center justify-center mb-6 group-hover:bg-primary-container transition-colors">
                <Icon
                  name="clean_hands"
                  className="material-symbols-outlined text-primary text-3xl group-hover:text-on-primary-container"
                />
              </div>
              <h3 className="font-headline-md text-headline-md text-on-surface mb-3">
                Practical Fiqh
              </h3>
              <p className="font-body-md text-body-md text-on-surface-variant">
                Understand basic rules of purification (Wudu), prayer (Salah), fasting (Sawm), and daily worship guidelines.
              </p>
            </div>

            <div className="group card-pad rounded-2xl border border-outline-variant hover:border-primary hover:shadow-xl transition-all duration-300 scroll-reveal">
              <div className="w-16 h-16 bg-surface-container rounded-full flex items-center justify-center mb-6 group-hover:bg-primary-container transition-colors">
                <Icon
                  name="history"
                  className="material-symbols-outlined text-primary text-3xl group-hover:text-on-primary-container"
                />
              </div>
              <h3 className="font-headline-md text-headline-md text-on-surface mb-3">
                Prophetic Seerah
              </h3>
              <p className="font-body-md text-body-md text-on-surface-variant">
                Model your life after the beautiful character and life events of the Prophet Muhammad (PBUH).
              </p>
            </div>

            <div className="group card-pad rounded-2xl border border-outline-variant hover:border-primary hover:shadow-xl transition-all duration-300 scroll-reveal">
              <div className="w-16 h-16 bg-surface-container rounded-full flex items-center justify-center mb-6 group-hover:bg-primary-container transition-colors">
                <Icon
                  name="diversity_1"
                  className="material-symbols-outlined text-primary text-3xl group-hover:text-on-primary-container"
                />
              </div>
              <h3 className="font-headline-md text-headline-md text-on-surface mb-3">
                Islamic Akhlaq
              </h3>
              <p className="font-body-md text-body-md text-on-surface-variant">
                Develop excellent moral character, practicing respect for parents, honesty, kindness, and community values.
              </p>
            </div>

            <div className="group card-pad rounded-2xl border border-outline-variant hover:border-primary hover:shadow-xl transition-all duration-300 scroll-reveal">
              <div className="w-16 h-16 bg-surface-container rounded-full flex items-center justify-center mb-6 group-hover:bg-primary-container transition-colors">
                <Icon
                  name="chat_bubble"
                  className="material-symbols-outlined text-primary text-3xl group-hover:text-on-primary-container"
                />
              </div>
              <h3 className="font-headline-md text-headline-md text-on-surface mb-3">
                Hadith &amp; Duas
              </h3>
              <p className="font-body-md text-body-md text-on-surface-variant">
                Memorize essential daily supplications (Duas) and short sayings (Ahadith) of the Prophet for spiritual mindfulness.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="section-y bg-surface-container-low">
        <div className="page-x max-w-max-width mx-auto flex flex-col md:flex-row items-center gap-8 md:gap-16">
          <div className="flex-1 space-y-8">
            <h2 className="text-headline-responsive text-primary">
              Who is this program for?
            </h2>
            <div className="space-y-6">
              <div className="flex gap-4">
                <div className="mt-1 flex-shrink-0 w-10 h-10 rounded-full bg-tertiary-fixed flex items-center justify-center">
                  <Icon
                    name="done"
                    className="material-symbols-outlined text-on-tertiary-fixed"
                  />
                </div>
                <div>
                  <h4 className="font-headline-md text-headline-md text-on-surface">
                    Muslim Youth &amp; Children
                  </h4>
                  <p className="font-body-md text-body-md text-on-surface-variant">
                    Designed to instill strong identity, correct values, and essential religious knowledge in kids and teens.
                  </p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="mt-1 flex-shrink-0 w-10 h-10 rounded-full bg-primary-fixed flex items-center justify-center">
                  <Icon
                    name="done"
                    className="material-symbols-outlined text-on-primary-fixed"
                  />
                </div>
                <div>
                  <h4 className="font-headline-md text-headline-md text-on-surface">
                    Adult Beginners &amp; Converts
                  </h4>
                  <p className="font-body-md text-body-md text-on-surface-variant">
                    Adults seeking a structured, step-by-step introduction to the fundamental knowledge and daily practices of Islam.
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="flex-1 bg-white p-8 rounded-3xl border border-outline-variant shadow-lg space-y-6">
            <h3 className="font-headline-md text-primary text-center">Core Pillars Covered</h3>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <Icon name="check_circle" className="text-primary mt-1" />
                <div>
                  <span className="font-bold text-on-surface">Aqeedah (Beliefs)</span>
                  <p className="font-body-md text-on-surface-variant">Understanding the six pillars of faith (Iman) and correct Islamic beliefs.</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Icon name="check_circle" className="text-primary mt-1" />
                <div>
                  <span className="font-bold text-on-surface">Seerah &amp; Stories of Prophets</span>
                  <p className="font-body-md text-on-surface-variant">Lessons from the lives of the Prophets to inspire righteous actions.</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Icon name="check_circle" className="text-primary mt-1" />
                <div>
                  <span className="font-bold text-on-surface">Social Etiquette (Adab)</span>
                  <p className="font-body-md text-on-surface-variant">Teaching kindness, sincerity, modesty, and healthy community relations.</p>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </section>

      <section className="section-y page-x max-w-max-width mx-auto">
        <div className="gradient-mesh rounded-section p-8 md:p-16 flex flex-col md:flex-row items-center justify-between border border-primary-fixed/30 gap-8">
          <div className="max-w-xl">
            <h2 className="text-display-responsive text-primary mb-4 font-bold">
              Ready to learn the foundations of Islam?
            </h2>
            <p className="text-body-lg text-on-surface-variant">
              Register now and begin building correct spiritual character and sound religious knowledge under our expert teachers.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 w-full md:w-auto">
            <Link
              to="/contact"
              className="bg-primary text-on-primary px-8 py-4 rounded-full text-center font-bold shadow-lg hover:shadow-xl hover:scale-105 active:scale-95 transition-all"
            >
              Book Free Trial
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
